import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AgentConfigSummary,
  AgentGlossaryKind,
  AgentGlossaryTerm,
  AgentPromptVersion,
} from '@magimp/shared';
import {
  activateAgentPromptVersion,
  approveAgentGlossaryTerm,
  archiveAgentGlossaryTerm,
  createAgentGlossaryTerm,
  createAgentPromptDraft,
  getAgentConfigSummary,
  listAgentGlossaryTerms,
  listAgentPromptVersions,
  rejectAgentGlossaryTerm,
} from '../../api/agent/index.js';
import { Button, Input, Select, Textarea } from '../../ui/index.js';
import { AgentMemoryPanel } from './AgentMemoryPanel.js';

export function AgentAdminPanel() {
  const { t } = useTranslation('agent');
  const [summary, setSummary] = useState<AgentConfigSummary | null>(null);
  const [prompts, setPrompts] = useState<AgentPromptVersion[]>([]);
  const [terms, setTerms] = useState<AgentGlossaryTerm[]>([]);
  const [promptDraft, setPromptDraft] = useState('');
  const [term, setTerm] = useState('');
  const [definition, setDefinition] = useState('');
  const [kind, setKind] = useState<AgentGlossaryKind>('glossary');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const [summaryResult, promptResult, glossaryResult] = await Promise.all([
        getAgentConfigSummary(),
        listAgentPromptVersions(),
        listAgentGlossaryTerms(),
      ]);
      setSummary(summaryResult);
      setPrompts(promptResult.items);
      setTerms(glossaryResult.items);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('admin.loadFailed'));
    } finally {
      setBusy(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const savePrompt = async () => {
    const content = promptDraft.trim();
    if (content === '') return;
    setBusy(true);
    try {
      await createAgentPromptDraft({ content });
      setPromptDraft('');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const saveTerm = async () => {
    const trimmedTerm = term.trim();
    const trimmedDefinition = definition.trim();
    if (trimmedTerm === '' || trimmedDefinition === '') return;
    setBusy(true);
    try {
      await createAgentGlossaryTerm({ kind, term: trimmedTerm, definition: trimmedDefinition });
      setTerm('');
      setDefinition('');
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      data-testid="agent-admin-panel"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--s-3)',
        borderTop: '1px solid var(--border-1)',
        paddingTop: 'var(--s-3)',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 'var(--s-2)' }}>
        <h2 style={{ fontSize: 16, margin: 0, flex: '1 1 auto' }}>{t('admin.title')}</h2>
        <Button kind="secondary" icon="rotate-ccw" onClick={load} disabled={busy}>
          {t('admin.refresh')}
        </Button>
      </header>

      {summary && (
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 'var(--s-2)',
            margin: 0,
            fontSize: 12,
          }}
        >
          <Metric label={t('admin.activePrompt')} value={summary.activePromptVersionId ?? '—'} />
          <Metric label={t('admin.glossaryCount')} value={String(summary.approvedGlossaryCount)} />
          <Metric label={t('admin.metricCount')} value={String(summary.approvedMetricCount)} />
          <Metric
            label={t('admin.memoryState')}
            value={summary.memoryEnabled ? t('admin.enabled') : t('admin.disabled')}
          />
          <Metric label={t('admin.catalogVersion')} value={summary.catalogVersion} />
        </dl>
      )}

      {error && <p style={{ color: 'var(--status-error-fg)', margin: 0 }}>{error}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 'var(--s-2)' }}>
        <Textarea
          value={promptDraft}
          rows={3}
          onChange={setPromptDraft}
          ariaLabel={t('admin.promptDraft')}
          placeholder={t('admin.promptPlaceholder')}
        />
        <Button kind="secondary" icon="save" onClick={savePrompt} disabled={busy}>
          {t('admin.savePromptDraft')}
        </Button>
      </div>

      <div
        style={{ display: 'grid', gridTemplateColumns: '130px minmax(0,1fr)', gap: 'var(--s-2)' }}
      >
        <Select
          value={kind}
          onChange={(value) => setKind(value === 'metric' ? 'metric' : 'glossary')}
          options={[
            { value: 'glossary', label: t('admin.kindGlossary') },
            { value: 'metric', label: t('admin.kindMetric') },
          ]}
        />
        <Input value={term} onChange={setTerm} placeholder={t('admin.termPlaceholder')} />
        <div />
        <Textarea
          value={definition}
          rows={2}
          onChange={setDefinition}
          ariaLabel={t('admin.definition')}
          placeholder={t('admin.definitionPlaceholder')}
        />
        <div />
        <Button kind="secondary" icon="plus" onClick={saveTerm} disabled={busy}>
          {t('admin.addDraft')}
        </Button>
      </div>

      <AdminList
        title={t('admin.prompts')}
        items={prompts}
        render={(prompt) => (
          <Row key={prompt.id}>
            <span>{prompt.status}</span>
            <code>{prompt.id.slice(0, 8)}</code>
            <Button
              kind="ghost"
              icon="check"
              disabled={busy || prompt.status === 'active'}
              onClick={() => void activateAgentPromptVersion(prompt.id).then(load)}
            >
              {t('admin.activate')}
            </Button>
          </Row>
        )}
      />

      <AdminList
        title={t('admin.glossary')}
        items={terms}
        render={(item) => (
          <Row key={item.id}>
            <strong>{item.term}</strong>
            <span>{item.kind}</span>
            <span>{item.status}</span>
            <Button
              kind="ghost"
              icon="check"
              disabled={busy || item.status === 'approved'}
              onClick={() => void approveAgentGlossaryTerm(item.id).then(load)}
            >
              {t('admin.approve')}
            </Button>
            <Button
              kind="ghost"
              icon="x"
              disabled={busy || item.status === 'rejected'}
              onClick={() => void rejectAgentGlossaryTerm(item.id).then(load)}
            >
              {t('admin.reject')}
            </Button>
            <Button
              kind="ghost"
              icon="archive"
              disabled={busy || item.status === 'archived'}
              onClick={() => void archiveAgentGlossaryTerm(item.id).then(load)}
            >
              {t('admin.archive')}
            </Button>
          </Row>
        )}
      />

      <AgentMemoryPanel />
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ minWidth: 0 }}>
      <dt style={{ color: 'var(--fg-3)' }}>{label}</dt>
      <dd style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</dd>
    </div>
  );
}

function AdminList<T>({
  title,
  items,
  render,
}: {
  title: string;
  items: readonly T[];
  render: (item: T) => ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-1)' }}>
      <h3 style={{ fontSize: 14, margin: 0 }}>{title}</h3>
      {items.map(render)}
    </div>
  );
}

function Row({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--s-2)',
        flexWrap: 'wrap',
        minHeight: 34,
        fontSize: 12,
      }}
    >
      {children}
    </div>
  );
}
